import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Play, RefreshCw, Target, X } from "lucide-react";
import {
  api,
  type SourceProposalOutcome,
  type SourceProposalStatus,
  type SourceProposalView,
} from "../../lib/api.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { EmptyState } from "../../components/ui/EmptyState.js";

/**
 * The source-intake inbox: what competitor pages already bring them traffic,
 * as topics we could write — each one checked against what we already have.
 *
 * Nothing here is a keyword yet. Approve turns a topic into pipeline work:
 *   new     a new keyword (primary + the competitor page's other queries as
 *           secondaries), placed in a cluster like every new keyword
 *   join    the same, filed into the cluster it overlaps
 *   method  the same, filed into the treatment page's list (track 2)
 *   extend  the competitor's queries are added to the live page's keyword;
 *           "Approve as new" overrides when the matched page is too narrow
 */

const OUTCOME_LABEL: Record<string, { label: string; tone: string; what: string }> = {
  new: { label: "New", tone: "bg-emerald-100 text-emerald-800", what: "No page of ours covers it" },
  join: { label: "Join", tone: "bg-sky-100 text-sky-800", what: "Overlaps a keyword we already have: files into that cluster" },
  method: { label: "Method", tone: "bg-violet-100 text-violet-800", what: "A treatment method's name: belongs to its /categories page (track 2)" },
  extend: { label: "Extend", tone: "bg-amber-100 text-amber-800", what: "A live page of ours already answers it" },
};
const RELEVANCE_TONE: Record<string, string> = {
  serve: "text-emerald-700",
  adjacent: "text-amber-700",
  out: "text-red-700",
};

export function SourceProposalsCard() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<SourceProposalStatus>("pending");
  const [outcome, setOutcome] = useState<SourceProposalOutcome | "all">("all");
  const [includeOut, setIncludeOut] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [note, setNote] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["source-intake", "proposals", status, outcome, includeOut],
    queryFn: () => api.listSourceProposals({ status, outcome: outcome === "all" ? undefined : outcome, includeOut }),
  });
  const st = useQuery({
    queryKey: ["source-intake", "status"],
    queryFn: () => api.sourceIntakeStatus(),
    refetchInterval: (q) => (q.state.data?.running ? 4_000 : 60_000),
  });

  // Approved keywords the autopilot cannot write yet: no SERP. One button runs
  // the enrichment queue on exactly these (2 SerpAPI searches each).
  const unenriched = useQuery({
    queryKey: ["source-intake", "unenriched"],
    queryFn: () => api.sourceIntakeUnenriched(),
    refetchInterval: 60_000,
  });
  const enrichJob = useQuery({
    queryKey: ["source-intake", "enrich-job"],
    queryFn: () => api.getEnrichQueue(),
    refetchInterval: (q) => (q.state.data?.running ? 4_000 : 60_000),
  });
  const quota = useQuery({ queryKey: ["source-intake", "serp-quota"], queryFn: () => api.getSerpApiQuota(), staleTime: 300_000 });
  const enrich = useMutation({
    mutationFn: () => api.enrichKeywords((unenriched.data?.keywords ?? []).map((k) => k.id)),
    onSuccess: (r) => {
      setNote(r.started ? "Enrichment started — approved keywords become eligible for the autopilot as they finish." : r.message ?? r.error ?? "not started");
      void qc.invalidateQueries({ queryKey: ["source-intake"] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["source-intake"] });
    setSelected(new Set());
  };

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.runSourceIntake(dryRun),
    onSuccess: (r) => { setNote(r.started ? "Intake started in the background — this list refreshes when it finishes." : r.error ?? "not started"); void qc.invalidateQueries({ queryKey: ["source-intake", "status"] }); },
    onError: (e: Error) => setNote(e.message),
  });
  const decide = useMutation({
    mutationFn: (v: { decision: "approve" | "reject"; asNew?: boolean }) =>
      api.decideSourceProposals({ ids: [...selected], decision: v.decision, asNew: v.asNew }),
    onSuccess: (r, v) => {
      if (v.decision === "reject") setNote(`${r.rejected ?? 0} rejected.`);
      else {
        const ok = (r.results ?? []).filter((x) => x.ok).length;
        const refused = (r.results ?? []).filter((x) => !x.ok);
        setNote(
          `${ok} approved` +
            (r.placedByRules ? ` · ${r.placedByRules} placed in a cluster by rule` : "") +
            (r.queuedForPlacement ? ` · ${r.queuedForPlacement} queued for cluster placement` : "") +
            (refused.length ? ` · ${refused.length} refused: ${refused.map((x) => `#${x.proposalId} ${x.message}`).join("; ")}` : ""),
        );
      }
      refresh();
    },
    onError: (e: Error) => setNote(e.message),
  });

  const rows = list.data?.rows ?? [];
  const counts = list.data?.counts ?? {};
  const pendingOf = (o: string) => counts[`pending:${o}`] ?? 0;
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  const anyExtendSelected = selectedRows.some((r) => r.outcome === "extend");
  const last = st.data?.last;

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <SectionCard
      title="Competitor topics — proposals"
      subtitle={
        st.data?.running
          ? "Intake running…"
          : last
            ? `Last run ${new Date(last.at).toLocaleString("he-IL")} · ${last.topics} topics · +${last.inserted} new · $${last.costUsd.toFixed(2)}${st.data?.nextRunAt ? ` · next ${new Date(st.data.nextRunAt).toLocaleDateString("he-IL")}` : ""}`
            : `Weekly from competitor footprints${st.data?.nextRunAt ? ` · next ${new Date(st.data.nextRunAt).toLocaleDateString("he-IL")}` : ""}`
      }
      icon={Target}
      action={
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs" disabled={run.isPending || st.data?.running} onClick={() => run.mutate(false)} title="Read the competitors' ranked keywords now (~$0.15 per domain)">
            {st.data?.running ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run intake
          </button>
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        {(["pending", "approved", "rejected"] as const).map((s) => (
          <button key={s} className={`rounded-full px-2.5 py-1 ${status === s ? "bg-ink text-white" : "bg-canvas text-muted"}`} onClick={() => { setStatus(s); setSelected(new Set()); }}>
            {s}
          </button>
        ))}
        <span className="mx-1 text-line">|</span>
        {(["all", "new", "join", "method", "extend"] as const).map((o) => (
          <button key={o} className={`rounded-full px-2.5 py-1 ${outcome === o ? "bg-brand text-white" : "bg-canvas text-muted"}`} onClick={() => { setOutcome(o); setSelected(new Set()); }} title={o === "all" ? undefined : OUTCOME_LABEL[o]?.what}>
            {o === "all" ? "all" : OUTCOME_LABEL[o]!.label}
            {o !== "all" && status === "pending" ? ` ${pendingOf(o)}` : ""}
          </button>
        ))}
        <label className="ml-2 flex items-center gap-1 text-muted">
          <input type="checkbox" checked={includeOut} onChange={(e) => setIncludeOut(e.target.checked)} /> show out-of-scope
        </label>
      </div>

      {status === "pending" && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-xs">
          <span className="font-medium">{selected.size} selected</span>
          <button className="btn-primary text-xs" disabled={decide.isPending} onClick={() => decide.mutate({ decision: "approve" })}>
            <Check className="h-3.5 w-3.5" /> Approve
          </button>
          {anyExtendSelected && (
            <button className="btn-ghost text-xs" disabled={decide.isPending} onClick={() => decide.mutate({ decision: "approve", asNew: true })} title="Create a new article even though a live page matched (when that page is a narrower spoke)">
              Approve as new article
            </button>
          )}
          <button className="btn-ghost text-xs" disabled={decide.isPending} onClick={() => decide.mutate({ decision: "reject" })}>
            <X className="h-3.5 w-3.5" /> Reject
          </button>
        </div>
      )}
      {(unenriched.data?.count ?? 0) > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span>
            <b>{unenriched.data!.count}</b> approved keyword{unenriched.data!.count === 1 ? "" : "s"} not enriched yet — the autopilot skips them until they are
            {" "}(~{unenriched.data!.count * 2} SerpAPI searches
            {quota.data?.quota ? `, ${quota.data.quota.totalSearchesLeft} left this month` : ""}).
          </span>
          {enrichJob.data?.running && enrichJob.data.job ? (
            <span className="font-medium">
              Enriching {enrichJob.data.job.done}/{enrichJob.data.job.total}
              {enrichJob.data.job.current ? ` · ${enrichJob.data.job.current}` : ""}
            </span>
          ) : (
            <button className="btn-secondary text-xs" disabled={enrich.isPending} onClick={() => enrich.mutate()}>
              Enrich them
            </button>
          )}
        </div>
      )}
      {note && <div className="mb-3 rounded-lg border border-line bg-white px-3 py-2 text-xs text-ink">{note}</div>}

      {list.isPending ? (
        <div className="py-6 text-center text-sm text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState title="Nothing here" description={status === "pending" ? "Run the intake to read the competitors' ranked keywords." : `No ${status} proposals.`} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-muted">
                {status === "pending" && (
                  <th className="w-8 py-2">
                    <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))} />
                  </th>
                )}
                <th className="py-2">Topic</th>
                <th className="py-2 text-right">Demand /mo</th>
                <th className="py-2 text-right">Competitor</th>
                <th className="py-2">What we have</th>
                <th className="py-2">Fit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ProposalRow key={r.id} r={r} selectable={status === "pending"} checked={selected.has(r.id)} onToggle={() => toggle(r.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function ProposalRow({ r, selectable, checked, onToggle }: { r: SourceProposalView; selectable: boolean; checked: boolean; onToggle: () => void }) {
  const [open, setOpen] = useState(false);
  const meta = OUTCOME_LABEL[r.outcome];
  const secondaries = r.keywords.slice(1);
  return (
    <>
      <tr className="border-b border-line/60 align-top">
        {selectable && (
          <td className="py-2"><input type="checkbox" checked={checked} onChange={onToggle} /></td>
        )}
        <td className="py-2 pr-3">
          <button className="text-right font-medium text-ink hover:underline" dir="rtl" onClick={() => setOpen(!open)}>
            {r.primaryKeyword}
          </button>
          {secondaries.length > 0 && (
            <div className="mt-0.5 text-xs text-muted" dir="rtl">
              {secondaries.slice(0, 3).map((k) => k.keyword).join(" · ")}
              {secondaries.length > 3 ? ` · +${secondaries.length - 3}` : ""}
            </div>
          )}
          {r.discoveryId && <div className="mt-0.5 text-[11px] text-brand-700">from RSS item #{r.discoveryId}</div>}
        </td>
        <td className="py-2 text-right tabular-nums">{r.demand.toLocaleString()}</td>
        <td className="py-2 text-right text-xs text-muted">
          {r.bestPosition != null ? `#${r.bestPosition}` : "new page"}
          {r.etv > 0 ? ` · ~${r.etv}/mo` : ""}
          <div>{r.sources.join(", ")}</div>
        </td>
        <td className="py-2 pr-3 text-xs">
          {meta && <span className={`rounded px-1.5 py-0.5 font-medium ${meta.tone}`} title={meta.what}>{meta.label}</span>}
          {r.detail && <div className="mt-1 text-muted" dir="auto">{r.detail}</div>}
          {r.targetClusterId && <div className="mt-0.5 font-mono text-[11px] text-muted">{r.targetClusterId}</div>}
          {r.ourUrl && (
            <a className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-brand-700 hover:underline" href={r.ourUrl} target="_blank" rel="noreferrer">
              our page <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {r.status !== "pending" && <div className="mt-1 text-muted">{r.status}{r.appliedKeywordId ? ` → ${r.appliedKeywordId}` : ""}</div>}
        </td>
        <td className="py-2 text-xs">
          {r.relevance ? <span className={`font-medium ${RELEVANCE_TONE[r.relevance]}`}>{r.relevance}</span> : <span className="text-muted">—</span>}
          {r.relevanceReason && <div className="text-muted">{r.relevanceReason}</div>}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-line/60 bg-canvas-soft">
          <td colSpan={selectable ? 6 : 5} className="px-3 py-2 text-xs">
            <table className="w-full">
              <tbody>
                {r.keywords.map((k) => (
                  <tr key={k.keyword}>
                    <td className="py-0.5" dir="rtl">{k.keyword}</td>
                    <td className="py-0.5 text-right tabular-nums">{k.volume.toLocaleString()}/mo</td>
                    <td className="py-0.5 text-right text-muted">{k.position != null ? `#${k.position}` : ""}</td>
                    <td className="py-0.5 text-right text-muted">{k.kd != null ? `KD ${k.kd}` : ""}</td>
                    <td className="py-0.5 text-muted">{k.existing ? `ours (${k.existing.status})` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 flex flex-wrap gap-2">
              {r.competitorUrls.map((u) => (
                <a key={u} className="inline-flex items-center gap-1 text-brand-700 hover:underline" href={u} target="_blank" rel="noreferrer">
                  {safeDecode(u).replace(/^https?:\/\/(www\.)?/, "").slice(0, 60)} <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function safeDecode(u: string): string {
  try { return decodeURI(u); } catch { return u; }
}
